import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { createRole } from "api/Roles";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { BackArrowIcon } from "components/svg";
import Checkbox from "components/forms/Checkbox";
import TextInput from "components/forms/TextInput";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { CheckMarkIcon } from "components/svg";
import {
  RolesTableWrapper,
  RolesTableLabel,
  RoleTable,
  RoleLinkWrapper,
  RoleSelectWrapper,
  RoleLink,
  ModelTitle,
  RoleSelectInner,
  RoleGroup,
  RoleGroupOptions,
  RoleGroupTitle,
  RolesTableFooter,
  BasicRoleInputs,
  RoleCheckWrapper,
  PermissionDescription,
} from "./styled";
import { Link } from "theme-ui";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";

export default function RoleCreate() {
  const navigate = useNavigate();
  const toast = useToast();
  const [modelTitle, setModelTitle] = useState<string>("Company");
  const [model, setModel] = useState<string>("company");
  const { user } = useContext<any>(UserContext);

  /**
   * Proposal permissions. Same catalog as the edit screen — without it here, a
   * newly created role could not be given proposal access until someone went
   * back in and edited it.
   *
   * Reading the list stays on company access. Creating and sending are
   * separable because they are different trusts: building a priced proposal is
   * not the same act as putting it in front of a prospect.
   */
  const proposalPermissions: { key: string; label: string; description: string }[] = [
    { key: "proposalView",   label: "View",   description: "See the proposals list, tracking and the document that was sent." },
    { key: "proposalCreate", label: "Create", description: "Build, price and edit proposals, and regenerate a new version of one." },
    { key: "proposalSend",   label: "Send",   description: "Email a proposal to a prospect and send it on to additional recipients." },
  ];

  const leadPermissions: { key: string; label: string; description: string }[] = [
    { key: "leadView",    label: "View",    description: "See the leads list, a lead's record and its activity." },
    { key: "leadCreate",  label: "Create",  description: "Add leads by form, CSV import or from the proposal generator." },
    { key: "leadEdit",    label: "Edit",    description: "Change a lead's details, status, owner and next action; send quote links; mark lost; company Lead settings." },
    { key: "leadConvert", label: "Convert", description: "Turn a lead into a client, optionally inviting a portal user and creating the Zoho customer." },
  ];

  const _onSubmit = async (values: any) => {
    const permissions: string[] = [];

    if (values.settingsFullAccess) {
      permissions.push("companyAccess");
    } else {
      Object.keys(values).forEach((key) => {
        if (
          key.startsWith("settings") && 
          key !== "settingsFullAccess" && 
          !key.includes("FullAccess") && 
          !key.includes("NoAccess") && 
          values[key] === true
        ) {
          permissions.push(key);
        }
      });
    }

    Object.keys(values).forEach((key) => {
      if (
        !key.startsWith("settings") && 
        !key.includes("FullAccess") && 
        !key.includes("NoAccess") && 
        values[key] === true
      ) {
        permissions.push(key);
      }
    });

    const keys = ["title", "description"];
    const create = {
      ...Object.fromEntries(
        Object.entries(values).filter(([key]) => keys.includes(key))
      ),
      permissions, 
    };


    const res = await createRole({ data: create });
    if (res?.id) {
      toast.add("Role created.", "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "roles",
        action: "create_role",
        actionType: "create",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "role",
        entityId: res.id,
        metadata: null,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      navigate(`/roles/${res.id}`);
    } else {
      toast.add(
        res?.message ? res.message : "Error creating role.",
        "var(--theme-ui-colors-danger)"
      );
    }
    return true;
  };

  const ValidateSchema = Yup.object().shape({
    title: Yup.string().required("This field is required."),
  });

  const handleLinkClick = (model: string, modelTitle: string) => {
    setModel(model);
    setModelTitle(modelTitle);
  };

  return (
    <>
      <Header></Header>
      <Page
        title="Add Role"
        subtitle="Add a new role with specific permissions for your users."
        backAction={<Link variant="back" href="/roles"><BackArrowIcon />Back to Roles</Link>}
      >
        <Formik
          initialValues={{
            settingsFullAccess: true,
            proposalFullAccess: true,
            proposalView: true,
            proposalCreate: true,
            proposalSend: true,
            proposalNoAccess: false,
            leadFullAccess: true,
            leadView: true,
            leadCreate: true,
            leadEdit: true,
            leadConvert: true,
            leadNoAccess: false,
            userFullAccess: true,
            userCreate: true,
            userView: true,
            userEdit: true,
            userDelete: true,
            rfqCreate: true,
            rfqFullAccess: true,
            rfqEdit: true,
            rfqDelete: true,
            rfqView: true,
            roleFullAccess: true,
            roleView: true,
            roleCreate: true,
            roleEdit: true,
            roleDelete: true,
          }}
          onSubmit={_onSubmit}
          validationSchema={ValidateSchema}
        >
          {({
            isSubmitting,
            errors,
            values,
            submitCount,
            setFieldValue,
          }: any) => {
            const handleSimpleFullAccess = (val: any, model: string) => {
              if (val) {
                setFieldValue(`${model}NoAccess`, false);
                setFieldValue(`${model}Create`, true);
                setFieldValue(`${model}View`, true);
                setFieldValue(`${model}Edit`, true);
                setFieldValue(`${model}Delete`, true);
                setFieldValue(`${model}FullAccess`, true);
              }
            };

            const handleSimpleNoAccess = (val: any, model: string) => {
              if (val) {
                setFieldValue(`${model}FullAccess`, false);
                setFieldValue(`${model}Create`, false);
                setFieldValue(`${model}View`, false);
                setFieldValue(`${model}Edit`, false);
                setFieldValue(`${model}Delete`, false);
                setFieldValue(`${model}NoAccess`, true);
              }
            };

            const handleSimpleToggle = (
              val: boolean,
              name: string,
              model: string
            ) => {
              setFieldValue(name, val);

              const isAllUnchecked =
                (name === `${model}Create`
                  ? !val
                  : !values[`${model}Create`]) &&
                (name === `${model}View` ? !val : !values[`${model}View`]) &&
                (name === `${model}Edit` ? !val : !values[`${model}Edit`]) &&
                (name === `${model}Delete` ? !val : !values[`${model}Delete`]);

              const isAllChecked =
                (name === `${model}Create` ? val : values[`${model}Create`]) &&
                (name === `${model}View` ? val : values[`${model}View`]) &&
                (name === `${model}Edit` ? val : values[`${model}Edit`]) &&
                (name === `${model}Delete` ? val : values[`${model}Delete`]);

              if (!val) {
                setFieldValue(`${model}FullAccess`, false);
                setFieldValue(`${model}NoAccess`, isAllUnchecked);
              } else {
                setFieldValue(`${model}NoAccess`, false);
                setFieldValue(`${model}FullAccess`, isAllChecked);
              }
            };

            const handleComplexFullAccess = (
              val: any,
              name: string,
              model: string
            ) => {
              if (val) {
                setFieldValue(`${model}FullAccess`, true);
                setFieldValue(`${model}NoAccess`, false);
                setFieldValue(`${model}Create`, true);
                setFieldValue(`${model}Edit`, true);
                setFieldValue(`${model}View`, true);
                setFieldValue(`${model}Delete`, true);
                setFieldValue(`${model}MyEdit`, false);
                setFieldValue(`${model}MyView`, false);
                setFieldValue(`${model}MyDelete`, false);
              }
            };

            const handleComplexNoAccess = (
              val: any,
              name: string,
              model: string
            ) => {
              if (val) {
                setFieldValue(`${model}NoAccess`, true);
                setFieldValue(`${model}FullAccess`, false);
                setFieldValue(`${model}Create`, false);
                setFieldValue(`${model}Edit`, false);
                setFieldValue(`${model}View`, false);
                setFieldValue(`${model}Delete`, false);
                setFieldValue(`${model}MyDelete`, false);
                setFieldValue(`${model}MyView`, false);
                setFieldValue(`${model}MyEdit`, false);
              }
            };

            const handleComplexToggle = (
              val: boolean,
              name: string,
              model: string,
              action: string
            ) => {
              setFieldValue(name, val);
              const isAllUnchecked =
                (name === `${model}Create`
                  ? !val
                  : !values[`${model}Create`]) &&
                (name === `${model}View` ? !val : !values[`${model}View`]) &&
                (name === `${model}Edit` ? !val : !values[`${model}Edit`]) &&
                (name === `${model}Delete`
                  ? !val
                  : !values[`${model}Delete`]) &&
                (name === `${model}MyDelete`
                  ? !val
                  : !values[`${model}MyDelete`]) &&
                (name === `${model}MyEdit`
                  ? !val
                  : !values[`${model}MyEdit`]) &&
                (name === `${model}MyView` ? !val : !values[`${model}MyView`]);

              const isAllChecked =
                (name === `${model}Create` ? val : values[`${model}Create`]) &&
                (name === `${model}View` ? val : values[`${model}View`]) &&
                (name === `${model}Edit` ? val : values[`${model}Edit`]) &&
                (name === `${model}Delete` ? val : values[`${model}Delete`]);

              if (!val) {
                setFieldValue(`${model}FullAccess`, false);
                setFieldValue(`${model}NoAccess`, isAllUnchecked);
              } else {
                setFieldValue(`${model}NoAccess`, false);
                setFieldValue(`${model}FullAccess`, isAllChecked);

                if (name === `${model}${action}`) {
                  setFieldValue(`${model}My${action}`, false);
                  setFieldValue(`${model}${action}`, true);
                } else {
                  setFieldValue(`${model}${action}`, false);
                  setFieldValue(`${model}My${action}`, true);
                }
              }

              if (name.startsWith(`${model}My`)) {
                setFieldValue(`${model}FullAccess`, false); // Always uncheck FullAccess if a My field is toggled
                setFieldValue(`${model}${action}`, false);
              }
            };

            return (
              <Form noValidate>
                <BasicRoleInputs>
                  <TextInput
                    name="title"
                    label="Role Name"
                    value={values.title}
                    onChange={(val: any) => setFieldValue("title", val)}
                    $errors={
                      errors.title && submitCount > 0 ? errors.title : null
                    }
                    $customStyles={{ width: "100%", maxWidth: "400px" }}
                  />

                  <TextInput
                    name="description"
                    type="textarea"
                    label="Role Description"
                    value={values.description}
                    onChange={(val: any) => setFieldValue("description", val)}
                    $errors={
                      errors.description && submitCount > 0
                        ? errors.description
                        : null
                    }
                    $customStyles={{ width: "100%", maxWidth: "800px" }}
                    rows={1}
                  />
                </BasicRoleInputs>
                <RolesTableWrapper>
                  <RolesTableLabel>Permissions</RolesTableLabel>
                  <RoleTable>
                    <RoleLinkWrapper>
                      <RoleLink
                        className={
                          modelTitle === "Company" ? "active" : ""
                        }
                        onClick={() =>
                          handleLinkClick(
                            "company",
                            "Company"
                          )
                        }
                      >
                        Company
                      </RoleLink>
                      <RoleLink
                        className={modelTitle === "Users" ? "active" : ""}
                        onClick={() => handleLinkClick("users", "Users")}
                      >
                        Users
                      </RoleLink>
                      <RoleLink
                        className={modelTitle === "Roles" ? "active" : ""}
                        onClick={() => handleLinkClick("role", "Roles")}
                      >
                        Roles
                      </RoleLink>
                      <RoleLink
                        className={modelTitle === `${"RFQ's"}` ? "active" : ""}
                        onClick={() => handleLinkClick("rfq", `${"RFQ's"}`)}
                      >{`${"RFQ's"}`}</RoleLink>
                      <RoleLink
                        className={modelTitle === "Proposals" ? "active" : ""}
                        onClick={() => handleLinkClick("proposal", "Proposals")}
                      >Proposals</RoleLink>
                      <RoleLink
                        className={modelTitle === "Leads" ? "active" : ""}
                        onClick={() => handleLinkClick("lead", "Leads")}
                      >Leads</RoleLink>
                    </RoleLinkWrapper>
                    <RoleSelectWrapper>
                      <ModelTitle>{modelTitle}</ModelTitle>
                      <RoleSelectInner>
                        {model === "company" && (
                          <>
                            <RoleGroup>
                              <RoleGroupTitle>Full Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.settingsFullAccess}
                                  name="settingsFullAccess"
                                  onChange={(val: any) => {
                                    handleSimpleFullAccess(val, "settings");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>No Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.settingsNoAccess}
                                  name="settingsNoAccess"
                                  onChange={(val: any) => {
                                    handleSimpleNoAccess(val, "settings");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                          </>
                        )}
                        {model === "users" && (
                          <>
                            <RoleGroup>
                              <RoleGroupTitle>Full Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.userFullAccess}
                                  name="userFullAccess"
                                  onChange={(val: any) => {
                                    handleSimpleFullAccess(val, "user");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>No Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.userNoAccess}
                                  name="userNoAccess"
                                  onChange={(val: any) => {
                                    handleSimpleNoAccess(val, "user");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>Create</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.userCreate}
                                  name="userCreate"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(
                                      val,
                                      "userCreate",
                                      "user"
                                    );
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>View</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.userView}
                                  name="userView"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(val, "userView", "user");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>Edit</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.userEdit}
                                  name="userEdit"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(val, "userEdit", "user");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>Delete</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.userDelete}
                                  name="userDelete"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(
                                      val,
                                      "userDelete",
                                      "user"
                                    );
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                          </>
                        )}
                        {model === "rfq" && (
                          <>
                            <RoleGroup>
                              <RoleGroupTitle>Full Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.rfqFullAccess}
                                  name="rfqFullAccess"
                                  onChange={(val: any) => {
                                    handleComplexFullAccess(
                                      val,
                                      "rfqFullAccess",
                                      "rfq"
                                    );
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>No Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.rfqNoAccess}
                                  name="rfqNoAccess"
                                  onChange={(val: any) => {
                                    handleComplexNoAccess(
                                      val,
                                      "rfqNoAccess",
                                      "rfq"
                                    );
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>Create</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.rfqCreate}
                                  name="rfqCreate"
                                  onChange={(val: any) => {
                                    handleComplexToggle(
                                      val,
                                      "rfqCreate",
                                      "rfq",
                                      "Create"
                                    );
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle multi={true}>View</RoleGroupTitle>
                              <RoleGroupOptions>
                                <RoleCheckWrapper top={true}>
                                  <Checkbox
                                    width={"250px"}
                                    labelSize={"14px"}
                                    label="All RFQ's"
                                    checked={values.rfqView}
                                    name="rfqView"
                                    onChange={(val: any) => {
                                      handleComplexToggle(
                                        val,
                                        "rfqView",
                                        "rfq",
                                        "View"
                                      );
                                    }}
                                  />
                                  <PermissionDescription
                                    style={{ flex: "1 1 auto" }}
                                  >{`User will be able to view all RFQ's created by your company.`}</PermissionDescription>
                                </RoleCheckWrapper>
                                <RoleCheckWrapper>
                                  <Checkbox
                                    width={"250px"}
                                    labelSize={"14px"}
                                    label="RFQ's created by user"
                                    checked={values.rfqMyView}
                                    name="rfqMyView"
                                    onChange={(val: any) => {
                                      handleComplexToggle(
                                        val,
                                        "rfqMyView",
                                        "rfq",
                                        "View"
                                      );
                                    }}
                                  />
                                  <PermissionDescription
                                    style={{ flex: "1 1 auto" }}
                                  >{`User will be able to view all RFQ's created by them.`}</PermissionDescription>
                                </RoleCheckWrapper>
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle multi={true}>Edit</RoleGroupTitle>
                              <RoleGroupOptions>
                                <RoleCheckWrapper top={true}>
                                  <Checkbox
                                    width={"250px"}
                                    labelSize={"14px"}
                                    label="All RFQ's"
                                    checked={values.rfqEdit}
                                    name="rfqEdit"
                                    onChange={(val: any) => {
                                      handleComplexToggle(
                                        val,
                                        "rfqEdit",
                                        "rfq",
                                        "Edit"
                                      );
                                    }}
                                  />
                                  <PermissionDescription
                                    style={{ flex: "1 1 auto" }}
                                  >{`User will be able to edit all RFQ's created by your company.`}</PermissionDescription>
                                </RoleCheckWrapper>
                                <RoleCheckWrapper>
                                  <Checkbox
                                    width={"250px"}
                                    labelSize={"14px"}
                                    label="RFQ's created by user"
                                    checked={values.rfqMyEdit}
                                    name="rfqMyEdit"
                                    onChange={(val: any) => {
                                      handleComplexToggle(
                                        val,
                                        "rfqMyEdit",
                                        "rfq",
                                        "Edit"
                                      );
                                    }}
                                  />
                                  <PermissionDescription
                                    style={{ flex: "1 1 auto" }}
                                  >{`User will be able to edit all RFQ's created by them.`}</PermissionDescription>
                                </RoleCheckWrapper>
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle multi={true}>
                                Delete
                              </RoleGroupTitle>
                              <RoleGroupOptions>
                                <RoleCheckWrapper top={true}>
                                  <Checkbox
                                    width={"250px"}
                                    labelSize={"14px"}
                                    label="All RFQ's"
                                    checked={values.rfqDelete}
                                    name="rfqDelete"
                                    onChange={(val: any) => {
                                      handleComplexToggle(
                                        val,
                                        "rfqDelete",
                                        "rfq",
                                        "Delete"
                                      );
                                    }}
                                  />
                                  <PermissionDescription
                                    style={{ flex: "1 1 auto" }}
                                  >{`User will be able to delete all RFQ's created by your company.`}</PermissionDescription>
                                </RoleCheckWrapper>
                                <RoleCheckWrapper>
                                  <Checkbox
                                    width={"250px"}
                                    labelSize={"14px"}
                                    label="RFQ's created by user"
                                    checked={values.rfqMyDelete}
                                    name="rfqMyDelete"
                                    onChange={(val: any) => {
                                      handleComplexToggle(
                                        val,
                                        "rfqMyDelete",
                                        "rfq",
                                        "Delete"
                                      );
                                    }}
                                  />
                                  <PermissionDescription
                                    style={{ flex: "1 1 auto" }}
                                  >{`User will be able to delete all RFQ's created by them.`}</PermissionDescription>
                                </RoleCheckWrapper>
                              </RoleGroupOptions>
                            </RoleGroup>
                          </>
                        )}
                        {model === "role" && (
                          <>
                            <RoleGroup>
                              <RoleGroupTitle>Full Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.roleFullAccess}
                                  name="roleFullAccess"
                                  onChange={(val: any) => {
                                    handleSimpleFullAccess(val, "role");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>No Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.roleNoAccess}
                                  name="roleNoAccess"
                                  onChange={(val: any) => {
                                    handleSimpleNoAccess(val, "role");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>Create</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.roleCreate}
                                  name="roleCreate"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(
                                      val,
                                      "roleCreate",
                                      "role"
                                    );
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>View</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.roleView}
                                  name="roleView"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(val, "roleView", "role");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>Edit</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.roleEdit}
                                  name="roleEdit"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(val, "roleEdit", "role");
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>Delete</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.roleDelete}
                                  name="roleDelete"
                                  onChange={(val: any) => {
                                    handleSimpleToggle(
                                      val,
                                      "roleDelete",
                                      "role"
                                    );
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                          </>
                        )}
                        {model === "proposal" && (
                          <>
                            <RoleGroup>
                              <RoleGroupTitle>Full Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.proposalFullAccess}
                                  name="proposalFullAccess"
                                  onChange={(val: any) => {
                                    if (val) {
                                      setFieldValue("proposalFullAccess", true);
                                      setFieldValue("proposalNoAccess", false);
                                      proposalPermissions.forEach((p) => setFieldValue(p.key, true));
                                    }
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>No Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.proposalNoAccess}
                                  name="proposalNoAccess"
                                  onChange={(val: any) => {
                                    if (val) {
                                      setFieldValue("proposalNoAccess", true);
                                      setFieldValue("proposalFullAccess", false);
                                      proposalPermissions.forEach((p) => setFieldValue(p.key, false));
                                    }
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle multi={true}>Permissions</RoleGroupTitle>
                              <RoleGroupOptions>
                                {proposalPermissions.map((p, idx) => (
                                  <RoleCheckWrapper key={p.key} top={idx === 0}>
                                    <Checkbox
                                      width={"250px"}
                                      labelSize={"14px"}
                                      label={p.label}
                                      checked={!!values[p.key]}
                                      name={p.key}
                                      onChange={(val: any) => {
                                        setFieldValue(p.key, val);
                                        const nextAllChecked = proposalPermissions.every((x) =>
                                          x.key === p.key ? val : !!values[x.key],
                                        );
                                        setFieldValue("proposalFullAccess", nextAllChecked);
                                        if (val) setFieldValue("proposalNoAccess", false);
                                      }}
                                    />
                                    <PermissionDescription style={{ flex: "1 1 auto" }}>
                                      {p.description}
                                    </PermissionDescription>
                                  </RoleCheckWrapper>
                                ))}
                              </RoleGroupOptions>
                            </RoleGroup>
                            <div style={{ padding: "12px 20px", fontSize: 12, color: "#888", fontStyle: "italic" }}>
                              A super admin bypasses these entirely. Proposals carry provider cost
                              and margin, so these are for internal staff only — never granted to a
                              client or provider role.
                            </div>
                          </>
                        )}
                        {model === "lead" && (
                          <>
                            <RoleGroup>
                              <RoleGroupTitle>Full Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.leadFullAccess}
                                  name="leadFullAccess"
                                  onChange={(val: any) => {
                                    if (val) {
                                      setFieldValue("leadFullAccess", true);
                                      setFieldValue("leadNoAccess", false);
                                      leadPermissions.forEach((p) => setFieldValue(p.key, true));
                                    }
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle>No Access</RoleGroupTitle>
                              <RoleGroupOptions>
                                <Checkbox
                                  checked={values.leadNoAccess}
                                  name="leadNoAccess"
                                  onChange={(val: any) => {
                                    if (val) {
                                      setFieldValue("leadNoAccess", true);
                                      setFieldValue("leadFullAccess", false);
                                      leadPermissions.forEach((p) => setFieldValue(p.key, false));
                                    }
                                  }}
                                />
                              </RoleGroupOptions>
                            </RoleGroup>
                            <RoleGroup>
                              <RoleGroupTitle multi={true}>Permissions</RoleGroupTitle>
                              <RoleGroupOptions>
                                {leadPermissions.map((p, idx) => (
                                  <RoleCheckWrapper key={p.key} top={idx === 0}>
                                    <Checkbox
                                      width={"250px"}
                                      labelSize={"14px"}
                                      label={p.label}
                                      checked={!!values[p.key]}
                                      name={p.key}
                                      onChange={(val: any) => {
                                        setFieldValue(p.key, val);
                                        const nextAllChecked = leadPermissions.every((x) => (x.key === p.key ? val : !!values[x.key]));
                                        setFieldValue("leadFullAccess", nextAllChecked);
                                        if (val) setFieldValue("leadNoAccess", false);
                                      }}
                                    />
                                    <PermissionDescription style={{ flex: "1 1 auto" }}>{p.description}</PermissionDescription>
                                  </RoleCheckWrapper>
                                ))}
                              </RoleGroupOptions>
                            </RoleGroup>
                            <div style={{ padding: "12px 20px", fontSize: 12, color: "#888", fontStyle: "italic" }}>
                              Leads are internal only. The Leads menu appears when a role has View and the company has the Leads module switched on (Lead settings).
                            </div>
                          </>
                        )}
                      </RoleSelectInner>
                    </RoleSelectWrapper>
                  </RoleTable>
                  <RolesTableFooter>
                    <BasicButton
                      outline={true}
                      styles={{ margin: ".25rem 20px .25rem 0" }}
                      type="button"
                      onClick={() => navigate("/roles")}
                      scheme="secondary"
                    >
                      Cancel
                    </BasicButton>
                    <BasicButton
                      scheme="primary"
                      type="submit"
                      styles={{ margin: ".25rem 0px .25rem 0" }}
                      $submitting={isSubmitting}
                      $iconHeight="14px"
                      $loadingColor="#fff"
                      keepText={true}
                    >
                      {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}Save
                      Role
                    </BasicButton>
                  </RolesTableFooter>
                </RolesTableWrapper>
              </Form>
            );
          }}
        </Formik>
      </Page>
    </>
  );
}
